import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MediaUpload } from '@/components/common/MediaUpload';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import {
  ResponsiveCombobox,
  type ComboboxItem,
} from '@/components/ui/responsive-combobox';
import { useVenues } from '@/features/venues/hooks/useVenueManagement';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import {
  createOfferSchema,
  type CreateOfferInput,
} from '../../schemas/offerSchemas';

interface CreateOfferFormProps {
  onSubmit: (values: CreateOfferInput) => Promise<void>;
  formId?: string;
  onResetComplete?: () => void;
}

export function CreateOfferForm({
  onSubmit,
  formId = 'create-offer-form',
  onResetComplete,
}: CreateOfferFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const uploadMutation = useMediaUpload({ folder: 'offers' });

  // Fetch venues dynamically
  const { data: venueData, isLoading: isLoadingVenues } = useVenues();

  const venues: ComboboxItem[] = (venueData?.venues || []).map((venue) => ({
    value: venue.id.toString(),
    label: venue.name,
  }));

  const form = useForm({
    resolver: zodResolver(createOfferSchema),
    defaultValues: {
      venue_id: 0,
      short_description: '',
      detailed_description: '',
      media_url: '',
      expires_at: undefined,
      claim_frequency: 1,
      farming_time_in_hours: 12,
      is_active: true,
    },
  });

  const handleMediaUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      setIsUploading(false);
      return response;
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const handleSubmit = async (values: CreateOfferInput) => {
    try {
      await onSubmit(values);
      form.reset();
      onResetComplete?.();
    } catch {
      // Error is handled by the parent component (e.g., via toast in the mutation)
      // We don't reset the form on error so user can correct and retry
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        className="space-y-6"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-testid="offer-form"
      >
        <FormField
          control={form.control}
          name="venue_id"
          render={({ field }) => {
            const selectedVenue = venueData?.venues.find(
              (v) => v.id === field.value
            );
            return (
              <FormItem>
                <FormLabel>Venue *</FormLabel>
                <FormControl>
                  <ResponsiveCombobox
                    items={venues}
                    value={
                      field.value && field.value > 0
                        ? field.value.toString()
                        : undefined
                    }
                    onValueChange={(value) => {
                      field.onChange(value ? parseInt(value) : 0);
                    }}
                    placeholder={
                      isLoadingVenues ? 'Loading venues...' : 'Select venue'
                    }
                    searchPlaceholder="Search venues..."
                    disabled={isLoadingVenues}
                    data-testid="offer-form-venue-select"
                  />
                </FormControl>
                {selectedVenue && (
                  <div className="mt-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
                    <p className="font-medium">{selectedVenue.name}</p>
                    <p className="text-xs mt-1">
                      {selectedVenue.formatted_address}
                    </p>
                  </div>
                )}
                <FormDescription>
                  Select the venue this offer belongs to
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="short_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Description</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., 20% off all coffee drinks"
                  data-testid="offer-form-short-description"
                />
              </FormControl>
              <FormDescription>
                Brief description shown in offer lists (max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="detailed_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Detailed Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter full offer details, terms and conditions..."
                  rows={4}
                  data-testid="offer-form-detailed-description"
                />
              </FormControl>
              <FormDescription>
                Complete offer description with all details (max 1000
                characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="media_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Offer Image</FormLabel>
              <FormControl>
                <MediaUpload
                  value={field.value}
                  onChange={(url) => {
                    field.onChange(url);
                    // Trigger form validation for this field
                    form.trigger('media_url');
                  }}
                  onUpload={handleMediaUpload}
                  isUploading={isUploading}
                  onError={(error) =>
                    form.setError('media_url', { message: error })
                  }
                />
              </FormControl>
              <FormDescription>
                Upload an image for this offer (JPEG or PNG, max 5MB)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expires_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiry Date & Time</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  minDate={new Date()}
                  error={!!form.formState.errors.expires_at}
                />
              </FormControl>
              <FormDescription>
                When this offer expires (must be in the future)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="claim_frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Claim Frequency</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    max={100}
                    placeholder="1"
                    data-testid="offer-form-claim-frequency"
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? parseInt(e.target.value) : 1
                      )
                    }
                  />
                </FormControl>
                <FormDescription>Max claims per user (1-100)</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="farming_time_in_hours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Farming Time (hours)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={168}
                    placeholder="12"
                    data-testid="offer-form-farming-time"
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? parseInt(e.target.value) : 0
                      )
                    }
                  />
                </FormControl>
                <FormDescription>
                  Hours until redeemable (0-168)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active</FormLabel>
                <FormDescription>
                  Make this offer immediately available to users
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="offer-form-is-active"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
